// Reusable promo-code input pill used at the top of every signup surface.
// Auto-syncs to sessionStorage on every keystroke so the value survives
// the Google/Apple OAuth round-trip.

import { useEffect, useState } from "react";
import { Ticket, Check } from "lucide-react";
import { usePromoStatus } from "../lib/usePromoStatus";
import { storePendingPromoCode, readPendingPromoCode } from "../lib/promoRedeem";

const CODE = "CALM100";

export const PromoCodeField = ({ label = "Have an early-access code?" }) => {
  const { status } = usePromoStatus(CODE);
  const [value, setValue] = useState("");
  const [expanded, setExpanded] = useState(false);

  // Hydrate from sessionStorage on mount (returning after OAuth redirect).
  useEffect(() => {
    const stored = readPendingPromoCode();
    if (stored) {
      setValue(stored);
      setExpanded(true);
    }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase();
    setValue(v);
    storePendingPromoCode(v);
  };

  // Hide the field entirely when the code is exhausted — users can still
  // type "CALM100" but redemption will 409, and we don't want to imply
  // the promo is available.
  if (status && status.is_full) return null;

  const isKnownCode = value.trim().toUpperCase() === CODE;

  return (
    <div className="w-full" data-testid="promo-code-field">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-[#3090A8] hover:text-[#1B2349] underline underline-offset-2 py-1"
          data-testid="promo-code-toggle"
        >
          {label}
        </button>
      ) : (
        <div className="relative">
          <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F59E0B]" />
          <input
            type="text"
            value={value}
            onChange={handleChange}
            placeholder="Enter code (e.g. CALM100)"
            autoComplete="off"
            autoCapitalize="characters"
            className="w-full pl-9 pr-9 py-2.5 rounded-full text-sm font-mono tracking-widest bg-white border border-[#F59E0B]/30 focus:outline-none focus:border-[#F59E0B] placeholder:font-sans placeholder:tracking-normal placeholder:text-[#9A3412]/50"
            data-testid="promo-code-input"
          />
          {isKnownCode && (
            <Check
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#059669]"
              aria-label="Recognised code"
              data-testid="promo-code-recognised-check"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PromoCodeField;
